/* eslint-disable no-cond-assign */

import React from 'react';

import addDocName from './modifiers/addDocName';

import addSmartLink from './modifiers/addSmartLink';
import OnedriveLink from 'Shared/components/SmartLink/OnedriveLink';
import FigmaLink from 'Shared/components/SmartLink/FigmaLink';
import HubspotLink from 'Shared/components/SmartLink/HubspotLink';
import SalesForceLink from 'Shared/components/SmartLink/SalesForceLink';
import GoogleDocsLink from 'Shared/components/SmartLink/GoogleDocsLink';
import GoogleSheetsLink from 'Shared/components/SmartLink/GoogleSheetsLink';
import GooglePresentationLink from 'Shared/components/SmartLink/GooglePresentationLink';
import JiraIssueLink from 'Shared/components/SmartLink/JiraIssueLink';

export const smartLinksSchema = [
  {
    linkAnchor: null,
    entityType: 'SALESFORCE_SMART_LINK',
    urlRegexString: '[^ ]*salesforce.com[^ ]*|[^ ]*lightning.force.com[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'GOOGLE_DOCS_DOCUMENT_SMART_LINK',
    urlRegexString: '(^| )https://docs.google.com/document[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'GOOGLE_DOCS_SHEET_SMART_LINK',
    urlRegexString: '(^| )https://docs.google.com/spreadsheets[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'GOOGLE_DOCS_PRESENTATION_SMART_LINK',
    urlRegexString: '(^| )https://docs.google.com/presentation[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'SALESFORCE_SMART_LINK',
    urlRegexString: '[^ ]*lightning.force.com/lightning/r/Account[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'SALESFORCE_SMART_LINK',
    urlRegexString: '[^ ]*lightning.force.com/lightning/r/Opportunity[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'JIRA_ISSUE_SMART_LINK',
    urlRegexString: 'http[^ ]*.atlassian.net/(browse/|(secure/RapidBoard[^ ]*selectedIssue))[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'HUBSPOT_SMART_LINK',
    urlRegexString: '(^| )https://app.hubspot.com/contacts/[^ ]*/deal/[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'FIGMA_SMART_LINK',
    urlRegexString: '(^| )https://www.figma.com/file/[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'ONEDRIVE_SMART_LINK',
    urlRegexString: '[^ ]*sharepoint.com/[^ ]*sourcedoc=[^ ]*',
  },
];

const smartLinkStrategy = type => (contentBlock, callback, contentState) => {
  contentBlock.findEntityRanges(character => {
    const entityKey = character.getEntity();
    return entityKey !== null && contentState.getEntity(entityKey).getType() === type;
  }, callback);
};

function findWithRegex(regexString, contentBlock, callback) {
  const text = contentBlock.getText();
  const regex = new RegExp(regexString, 'g');
  let matchArr;
  let start;
  let end;
  let linkHref;
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;
    end = start + matchArr[0].length;
    linkHref = matchArr[0];
    callback(start, end, linkHref);
  }
}

function removeCharsAt(str, indexStart, indexEnd) {
  return str.substring(0, indexStart) + ''.padStart(indexEnd - indexStart, ' ') + str.substring(indexEnd);
}

function isTargetUrlExict(selectedBlock) {
  let text = selectedBlock.getText();
  // To prevent it from matching on text that is inside an entity already, we replace
  // text enclosed by an entity with spaces
  selectedBlock.findEntityRanges(
    character => character.getEntity() !== null,
    (start, end) => {
      text = removeCharsAt(text, start, end);
    }
  );
  return Boolean(smartLinksSchema.some(({ urlRegexString }) => new RegExp(urlRegexString, 'g').exec(text) !== null));
}

function modifyUrlBySmartLink(editorState) {
  const contentState = editorState.getCurrentContent();
  const selectedBlockKey = editorState.getSelection().getAnchorKey();
  const selectedBlock = contentState.getBlockForKey(selectedBlockKey);

  if (!isTargetUrlExict(selectedBlock)) return editorState;
  let newEditorState = editorState;
  smartLinksSchema.forEach(({ urlRegexString }) => {
    findWithRegex(urlRegexString, selectedBlock, (start, end, linkHref) => {
      newEditorState = addSmartLink(editorState, { start, end, linkHref, urlRegexString });
    });
  });

  return newEditorState;
}

const smartLinksPlugin = {
  onChange: modifyUrlBySmartLink,
  decorators: [
    {
      strategy: smartLinkStrategy('SALESFORCE_SMART_LINK'),
      component: props => <SalesForceLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('GOOGLE_DOCS_DOCUMENT_SMART_LINK'),
      component: props => <GoogleDocsLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('GOOGLE_DOCS_SHEET_SMART_LINK'),
      component: props => <GoogleSheetsLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('GOOGLE_DOCS_PRESENTATION_SMART_LINK'),
      component: props => <GooglePresentationLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('JIRA_ISSUE_SMART_LINK'),
      component: props => <JiraIssueLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('HUBSPOT_SMART_LINK'),
      component: props => <HubspotLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('FIGMA_SMART_LINK'),
      component: props => <FigmaLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('ONEDRIVE_SMART_LINK'),
      component: props => <OnedriveLink {...props} />,
    },
  ],
};

export { smartLinksPlugin, modifyUrlBySmartLink, addDocName };
